// src/pages/Partner/PartnerSupport.jsx



import {

  useCallback,

  useEffect,

  useMemo,

  useState,

} from "react";



import {

  AlertCircle,

  CheckCircle2,

  Clock3,

  History,

  Inbox,

  MessageSquareText,

  RefreshCw,

  UserRound,

  X,

} from "lucide-react";



import {

  Link,

  useOutletContext,

  useSearchParams,

} from "react-router-dom";



import {

  fetchMyPartnerSupportRequests,

  updateMyPartnerSupportRequestStatus,

} from "../../lib/partner/partnerSupport";



import PartnerGwinSupportPanel from "../../components/partner/PartnerGwinSupportPanel";





function formatDateTime(value) {

  if (!value) {

    return "Not available";

  }



  const date = new Date(value);



  if (

    Number.isNaN(

      date.getTime()

    )

  ) {

    return value;

  }



  return new Intl.DateTimeFormat(

    "en-ZA",

    {

      day: "2-digit",

      month: "short",

      year: "numeric",

      hour: "2-digit",

      minute: "2-digit",

    }

  ).format(date);

}





function statusLabel(status) {

  switch (status) {

    case "open":

      return "Open";



    case "acknowledged":

      return "Acknowledged";



    case "resolved":

      return "Resolved";



    case "closed":

      return "Closed";



    default:

      return status || "Unknown";

  }

}





function statusClasses(status) {

  switch (status) {

    case "open":

      return [

        "bg-red-50",

        "text-red-700",

        "border-red-200",

      ].join(" ");



    case "acknowledged":

      return [

        "bg-amber-50",

        "text-amber-800",

        "border-amber-200",

      ].join(" ");



    case "resolved":

      return [

        "bg-green-50",

        "text-green-700",

        "border-green-200",

      ].join(" ");



    case "closed":

      return [

        "bg-gray-100",

        "text-gray-700",

        "border-gray-200",

      ].join(" ");



    default:

      return [

        "bg-gray-50",

        "text-gray-700",

        "border-gray-200",

      ].join(" ");

  }

}





function roleLabel(role) {

  switch (role) {

    case "facilitator":

      return "Facilitator";



    case "cohort_manager":

      return "Cohort manager";



    default:

      return role || "Support team";

  }

}





function StatusBadge({

  status,

}) {

  return (

    <span

      className={[

        "inline-flex",

        "items-center",

        "rounded-full",

        "border",

        "px-2.5",

        "py-1",

        "text-xs",

        "font-bold",

        statusClasses(

          status

        ),

      ].join(" ")}

    >

      {statusLabel(

        status

      )}

    </span>

  );

}





export default function PartnerSupport() {

  const {

    access,

  } = useOutletContext();



  const [

    searchParams,

    setSearchParams,

  ] = useSearchParams();



  const [

    requests,

    setRequests,

  ] = useState([]);



  const [

    loading,

    setLoading,

  ] = useState(true);



  const [

    error,

    setError,

  ] = useState("");



  const [

    notice,

    setNotice,

  ] = useState("");



  const [

    changingRequestId,

    setChangingRequestId,

  ] = useState("");





  const canUseLearnerInbox =

    [

      "facilitator",

      "cohort_manager",

    ].includes(

      access?.partner_role

    );





  const selectedRequestId =

    searchParams.get(

      "request"

    ) || "";





  const loadRequests =

    useCallback(

      async () => {

        if (

  !canUseLearnerInbox

) {

  setRequests([]);

  setLoading(false);



  return;

}



        try {

          setLoading(true);

          setError("");



          const rows =

            await fetchMyPartnerSupportRequests();



          setRequests(

            Array.isArray(

              rows

            )

              ? rows

              : []

          );

        } catch (

          loadError

        ) {

          console.error(

            "Partner support requests loading failed:",

            loadError

          );



          setRequests([]);



          setError(

            loadError?.message ||

              "Support requests could not be loaded."

          );

        } finally {

          setLoading(

            false

          );

        }

      },

      [

        canUseLearnerInbox,

      ]

    );





  useEffect(() => {

    loadRequests();

  }, [loadRequests]);





  const needsSupport =

    useMemo(

      () =>

        requests.filter(

          (request) =>

            [

              "open",

              "acknowledged",

            ].includes(

              request.status

            )

        ),

      [requests]

    );





  const supportHistory =

    useMemo(

      () =>

        requests.filter(

          (request) =>

            [

              "resolved",

              "closed",

            ].includes(

              request.status

            )

        ),

      [requests]

    );





  const openCount =

    useMemo(

      () =>

        requests.filter(

          (request) =>

            request.status ===

            "open"

        ).length,

      [requests]

    );





  const acknowledgedCount =

    useMemo(

      () =>

        requests.filter(

          (request) =>

            request.status ===

            "acknowledged"

        ).length,

      [requests]

    );





  const selectedRequest =

    useMemo(

      () =>

        requests.find(

          (request) =>

            request.request_id ===

            selectedRequestId

        ) || null,

      [

        requests,

        selectedRequestId,

      ]

    );





  function openRequest(

    request

  ) {

    const next =

      new URLSearchParams(

        searchParams

      );



    next.set(

      "request",

      request.request_id

    );



    setSearchParams(

      next

    );

  }





  function closeRequest() {

    const next =

      new URLSearchParams(

        searchParams

      );



    next.delete(

      "request"

    );



    setSearchParams(

      next,

      {

        replace: true,

      }

    );

  }





  async function changeStatus(

    request,

    nextStatus

  ) {

    try {

      setChangingRequestId(

        request.request_id

      );



      setError("");

      setNotice("");



      const updated =

        await updateMyPartnerSupportRequestStatus(

          request.request_id,

          nextStatus

        );



      if (!updated) {

        throw new Error(

          "The support request did not return an updated status."

        );

      }



      setRequests(

        (current) =>

          current.map(

            (item) =>

              item.request_id ===

              request.request_id

                ? {

                    ...item,

                    ...updated,

                  }

                : item

          )

      );



      if (

        nextStatus ===

        "acknowledged"

      ) {

        setNotice(

          "Support request acknowledged."

        );

      }



      if (

        nextStatus ===

        "resolved"

      ) {

        setNotice(

          "Support request marked as resolved."

        );

      }



      if (

        nextStatus ===

        "closed"

      ) {

        setNotice(

          "Support request closed."

        );

      }

    } catch (

      updateError

    ) {

      console.error(

        "Partner support status update failed:",

        updateError

      );



      setError(

        updateError?.message ||

          "The support request could not be updated."

      );

    } finally {

      setChangingRequestId(

        ""

      );

    }

  }





  if (

  !canUseLearnerInbox

) {

  return (

    <div className="space-y-6">

      <PartnerGwinSupportPanel />

    </div>

  );

}





  return (

    <div className="space-y-6">



      <PartnerGwinSupportPanel />



      {/* Entrepreneur support inbox */}

      <section className="rounded-3xl border border-orange-100 bg-white p-6 shadow-sm sm:p-8">

        <div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">

          <div className="flex items-start gap-4">

            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-orange-50">

              <Inbox className="h-6 w-6 text-[#FF914D]" />

            </div>



            <div>

              <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#FF914D]">

                Partner · Support

              </p>



              <h1 className="mt-2 text-2xl font-black text-gray-900 sm:text-3xl">

                Entrepreneur support

              </h1>



              <p className="mt-2 max-w-3xl text-sm leading-6 text-gray-600">

                Review support requests that entrepreneurs have sent directly to you, acknowledge requests that you are working on, and keep a history of resolved support.

              </p>



              <p className="mt-2 text-xs font-semibold text-gray-500">

                Signed in as{" "}

                {roleLabel(

                  access?.partner_role

                )}

              </p>

            </div>

          </div>



          <button

            type="button"

            onClick={

              loadRequests

            }

            disabled={

              loading

            }

            className="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-bold text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"

          >

            <RefreshCw

              className={[

                "h-4",

                "w-4",

                loading

                  ? "animate-spin"

                  : "",

              ].join(" ")}

            />



            Refresh

          </button>

        </div>

      </section>





      {/* Messages */}

      {error && (

        <div className="flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">

          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />



          <span>

            {error}

          </span>

        </div>

      )}



      {notice && (

        <div className="flex items-start gap-3 rounded-2xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">

          <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" />



          <span>

            {notice}

          </span>

        </div>

      )}





      {/* Summary */}

      <section className="grid gap-4 sm:grid-cols-3">

        <SummaryCard

          icon={

            MessageSquareText

          }

          label="Open requests"

          value={

            openCount

          }

        />



        <SummaryCard

          icon={

            Clock3

          }

          label="Acknowledged"

          value={

            acknowledgedCount

          }

        />



        <SummaryCard

          icon={

            History

          }

          label="Support history"

          value={

            supportHistory.length

          }

        />

      </section>





      {loading ? (

        <section className="rounded-3xl border border-orange-100 bg-white p-10 text-center shadow-sm">

          <RefreshCw className="mx-auto h-6 w-6 animate-spin text-[#FF914D]" />



          <p className="mt-3 text-sm font-semibold text-gray-600">

            Loading support requests…

          </p>

        </section>

      ) : (

        <>

          {/* Needs support */}

          <SupportTableSection

            icon={

              MessageSquareText

            }

            title="Needs support"

            description="Open and acknowledged requests that still require attention."

            requests={

              needsSupport

            }

            emptyTitle="No support requests need attention"

            emptyDescription="New entrepreneur support requests sent directly to you will appear here."

            onOpen={

              openRequest

            }

          />





          {/* History */}

          <SupportTableSection

            icon={

              History

            }

            title="Support history"

            description="Resolved and closed entrepreneur support requests."

            requests={

              supportHistory

            }

            emptyTitle="No support history yet"

            emptyDescription="Resolved requests will move into this section automatically."

            onOpen={

              openRequest

            }

            history

          />

        </>

      )}





      {/* Request detail */}

      {selectedRequest && (

        <RequestDetailModal

          request={

            selectedRequest

          }

          changing={

            changingRequestId ===

            selectedRequest

              .request_id

          }

          onClose={

            closeRequest

          }

          onStatusChange={

            changeStatus

          }

        />

      )}

    </div>

  );

}





function SummaryCard({

  icon: Icon,

  label,

  value,

}) {

  return (

    <div className="rounded-2xl border border-orange-100 bg-white p-5 shadow-sm">

      <div className="flex items-center justify-between gap-4">

        <div>

          <p className="text-xs font-bold uppercase tracking-wide text-gray-500">

            {label}

          </p>



          <p className="mt-2 text-3xl font-black text-gray-900">

            {value}

          </p>

        </div>



        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-orange-50">

          <Icon className="h-5 w-5 text-[#FF914D]" />

        </div>

      </div>

    </div>

  );

}





function SupportTableSection({

  icon: Icon,

  title,

  description,

  requests,

  emptyTitle,

  emptyDescription,

  onOpen,

  history = false,

}) {

  return (

    <section className="overflow-hidden rounded-3xl border border-orange-100 bg-white shadow-sm">

      <div className="border-b border-gray-100 px-5 py-5 sm:px-6">

        <div className="flex items-start gap-3">

          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-orange-50">

            <Icon className="h-5 w-5 text-[#FF914D]" />

          </div>



          <div>

            <h2 className="text-lg font-black text-gray-900">

              {title}

            </h2>



            <p className="mt-1 text-sm text-gray-600">

              {description}

            </p>

          </div>

        </div>

      </div>





      {requests.length === 0 ? (

        <div className="px-6 py-10 text-center">

          <CheckCircle2 className="mx-auto h-9 w-9 text-gray-300" />



          <p className="mt-3 font-bold text-gray-800">

            {emptyTitle}

          </p>



          <p className="mx-auto mt-1 max-w-lg text-sm text-gray-500">

            {emptyDescription}

          </p>

        </div>

      ) : (

        <div className="overflow-x-auto">

          <table className="min-w-full divide-y divide-gray-100">

            <thead className="bg-gray-50/80">

              <tr>

                <TableHeading>

                  Entrepreneur

                </TableHeading>



                <TableHeading>

                  Cohort

                </TableHeading>



                <TableHeading>

                  Subject

                </TableHeading>



                <TableHeading>

                  {history

                    ? "Resolved"

                    : "Requested"}

                </TableHeading>



                <TableHeading>

                  Status

                </TableHeading>



                <TableHeading>

                  Action

                </TableHeading>

              </tr>

            </thead>



            <tbody className="divide-y divide-gray-100">

              {requests.map(

                (request) => (

                  <tr

                    key={

                      request.request_id

                    }

                    className="transition hover:bg-orange-50/40"

                  >

                    <td className="whitespace-nowrap px-5 py-4">

                      <div className="flex items-center gap-3">

                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-orange-50">

                          <UserRound className="h-4 w-4 text-[#FF914D]" />

                        </div>



                        <span className="text-sm font-bold text-gray-900">

                          {request.learner_full_name ||

                            "Entrepreneur"}

                        </span>

                      </div>

                    </td>



                    <td className="px-5 py-4">

                      <span className="text-sm text-gray-700">

                        {request.cohort_name ||

                          "Cohort"}

                      </span>

                    </td>



                    <td className="max-w-xs px-5 py-4">

                      <p className="truncate text-sm font-semibold text-gray-900">

                        {request.subject}

                      </p>

                    </td>



                    <td className="whitespace-nowrap px-5 py-4 text-sm text-gray-600">

                      {formatDateTime(

                        history

                          ? request.resolved_at ||

                              request.created_at

                          : request.created_at

                      )}

                    </td>



                    <td className="whitespace-nowrap px-5 py-4">

                      <StatusBadge

                        status={

                          request.status

                        }

                      />

                    </td>



                    <td className="whitespace-nowrap px-5 py-4">

                      <button

                        type="button"

                        onClick={() =>

                          onOpen(

                            request

                          )

                        }

                        className="rounded-xl border border-orange-200 bg-orange-50 px-3 py-2 text-xs font-bold text-orange-700 transition hover:bg-orange-100"

                      >

                        View request

                      </button>

                    </td>

                  </tr>

                )

              )}

            </tbody>

          </table>

        </div>

      )}

    </section>

  );

}





function TableHeading({

  children,

}) {

  return (

    <th

      scope="col"

      className="px-5 py-3 text-left text-xs font-bold uppercase tracking-wide text-gray-500"

    >

      {children}

    </th>

  );

}





function RequestDetailModal({

  request,

  changing,

  onClose,

  onStatusChange,

}) {

  return (

    <div

      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/40 px-4 py-6"

      role="dialog"

      aria-modal="true"

      aria-labelledby="support-request-title"

      onMouseDown={(

        event

      ) => {

        if (

          event.target ===

            event.currentTarget &&

          !changing

        ) {

          onClose();

        }

      }}

    >

      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-3xl bg-white shadow-2xl">

        <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-6 py-5">

          <div>

            <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#FF914D]">

              Entrepreneur support request

            </p>



            <h2

              id="support-request-title"

              className="mt-2 text-xl font-black text-gray-900"

            >

              {request.subject}

            </h2>

          </div>



          <button

            type="button"

            onClick={

              onClose

            }

            disabled={

              changing

            }

            className="rounded-xl p-2 text-gray-500 transition hover:bg-gray-100 hover:text-gray-900 disabled:opacity-50"

            aria-label="Close support request"

          >

            <X className="h-5 w-5" />

          </button>

        </div>





        <div className="space-y-6 px-6 py-6">

          <div className="grid gap-4 sm:grid-cols-2">

            <DetailItem

              label="Entrepreneur"

              value={

                request.learner_full_name ||

                "Entrepreneur"

              }

            />



            <DetailItem

              label="Cohort"

              value={

                request.cohort_name ||

                "Cohort"

              }

            />



            <DetailItem

              label="Requested"

              value={formatDateTime(

                request.created_at

              )}

            />



            <div>

              <p className="text-xs font-bold uppercase tracking-wide text-gray-500">

                Status

              </p>



              <div className="mt-2">

                <StatusBadge

                  status={

                    request.status

                  }

                />

              </div>

            </div>

          </div>





          <div>

            <p className="text-xs font-bold uppercase tracking-wide text-gray-500">

              Message

            </p>



            <div className="mt-2 rounded-2xl border border-gray-200 bg-gray-50 p-4">

              <p className="whitespace-pre-wrap text-sm leading-6 text-gray-800">

                {request.message}

              </p>

            </div>

          </div>





          {request.acknowledged_at && (

            <DetailItem

              label="Acknowledged"

              value={formatDateTime(

                request.acknowledged_at

              )}

            />

          )}





          {request.resolved_at && (

            <DetailItem

              label="Resolved"

              value={formatDateTime(

                request.resolved_at

              )}

            />

          )}





          <div className="flex flex-col gap-3 border-t border-gray-100 pt-5 sm:flex-row sm:items-center sm:justify-between">

            <Link

              to={`/partner/learners/${request.cohort_learner_id}`}

              className="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-bold text-gray-700 transition hover:bg-gray-50"

            >

              <UserRound className="h-4 w-4" />



              Open entrepreneur

            </Link>





            <div className="flex flex-col gap-2 sm:flex-row">

              {request.status ===

                "open" && (

                <button

                  type="button"

                  disabled={

                    changing

                  }

                  onClick={() =>

                    onStatusChange(

                      request,

                      "acknowledged"

                    )

                  }

                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm font-bold text-amber-800 transition hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-60"

                >

                  <Clock3 className="h-4 w-4" />



                  Acknowledge

                </button>

              )}





              {[

                "open",

                "acknowledged",

              ].includes(

                request.status

              ) && (

                <button

                  type="button"

                  disabled={

                    changing

                  }

                  onClick={() =>

                    onStatusChange(

                      request,

                      "resolved"

                    )

                  }

                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#FF914D] px-4 py-2.5 text-sm font-black text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"

                >

                  <CheckCircle2 className="h-4 w-4" />



                  Mark resolved

                </button>

              )}





              {request.status ===

                "resolved" && (

                <button

                  type="button"

                  disabled={

                    changing

                  }

                  onClick={() =>

                    onStatusChange(

                      request,

                      "closed"

                    )

                  }

                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm font-bold text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"

                >

                  <CheckCircle2 className="h-4 w-4" />



                  Close request

                </button>

              )}

            </div>

          </div>

        </div>

      </div>

    </div>

  );

}





function DetailItem({

  label,

  value,

}) {

  return (

    <div>

      <p className="text-xs font-bold uppercase tracking-wide text-gray-500">

        {label}

      </p>



      <p className="mt-1 text-sm font-semibold text-gray-900">

        {value}

      </p>

    </div>

  );

}
